/**
 * ElectronLayer - Renders lone pairs and radicals from SVG generation data
 *
 * Default visual rendering uses StaticLonePair (motion-enabled, dynamic LP positioning).
 */

import React, { memo } from 'react';
import { useSVGGenerationData } from '../../contexts/SVGGenerationContext';
import { StaticLonePair } from '../../motion/components/StaticLonePair';
import type { ElectronData, ChargeData } from '../../rendering/useSVGGeneration';

export interface ElectronLayerProps {
  render?: (
    electronData: ElectronData,
    allElectronData: ElectronData[],
    allChargeData: ChargeData[],
    index: number,
  ) => React.ReactNode;
  renderHitbox?: (
    electronData: ElectronData,
    allElectronData: ElectronData[],
    allChargeData: ChargeData[],
    index: number,
  ) => React.ReactNode;
}

// React.memo (PIPH-69): see AtomLayer for rationale.
export const ElectronLayer = memo(function ElectronLayer({ render, renderHitbox }: ElectronLayerProps) {
  const { electronData, chargeData } = useSVGGenerationData();

  return (
    <g data-layer="electrons">
      {electronData.map((electronDatum, index) => (
        <React.Fragment key={`${electronDatum.atomId}-electron-${index}`}>
          {renderHitbox && renderHitbox(electronDatum, electronData, chargeData, index)}
          {render
            ? render(electronDatum, electronData, chargeData, index)
            : <StaticLonePair electronData={electronDatum} allElectronData={electronData} allChargeData={chargeData} />
          }
        </React.Fragment>
      ))}
    </g>
  );
});
